import { useQuery } from '@apollo/client';
import { Tv } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { applyServerErrors } from '~/api/helpers';
import { useDeleteTVBrand } from '~/api/tv-brands/delete';
import { useUpdateTVBrand } from '~/api/tv-brands/update';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { NetworkTvBrandSettingsDocument } from '../queries/settings.generated';
import { TVBrandForm, getValidationSchema } from './components';
import { useTabs } from './lib/tabs';

export const TVBrandSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const tabs = useTabs();

  const params = useParams<{ tvBrandId: string }>();

  const tvBrandId = Number(params.tvBrandId);

  const { data, loading, error } = useQuery(NetworkTvBrandSettingsDocument, {
    variables: { networkId: currentNetwork.id, tvBrandId },
  });

  const [deleteTvBrand] = useDeleteTVBrand();
  const [updateTvBrand] = useUpdateTVBrand();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const tvBrand = data?.network?.tvBrand;

  const validationSchema = getValidationSchema(t);

  const initialValues = useMemo(
    () => ({
      baudRate: tvBrand?.baudRate.toString() ?? '',
      input1: tvBrand?.input1 ?? '',
      input2: tvBrand?.input2 ?? '',
      input3: tvBrand?.input3 ?? '',
      mute: tvBrand?.mute ?? '',
      name: tvBrand?.name ?? '',
      powerOff: tvBrand?.powerOff ?? '',
      powerOn: tvBrand?.powerOn ?? '',
      status: tvBrand?.status ?? '',
      unmute: tvBrand?.unmute ?? '',
      volumeDown: tvBrand?.volumeDown ?? '',
      volumeUp: tvBrand?.volumeUp ?? '',
    }),
    [tvBrand],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const newValues = validationSchema.cast(values);
      const result = await updateTvBrand({
        variables: { patch: { ...newValues, baudRate: Number(newValues.baudRate) }, id: tvBrandId },
      });
      applyServerErrors(result, setErrors);
    },
    validateOnMount: true,
    validationSchema,
  });

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteTvBrand({
      variables: { input: { id: tvBrandId } },
    });
    navigate(link('/tv-brands'), { replace: true });
  }, [confirmDelete, deleteTvBrand, link, navigate, tvBrandId]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  if (!tvBrand) return null;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="tv-brand-form"
              />
              <Tooltip title={tvBrand.canDestroy.reasons?.fullMessages.join(', ')}>
                <span>
                  <DeleteButton disabled={!tvBrand.canDestroy.value} onClick={handleDelete} />
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<Tv />}
          titleText={tvBrand.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

        <PageContainer>
          <TVBrandForm formik={formik} formId="tv-brand-form" readonly={!tvBrand.canUpdate.value} />

          {currentUser.admin && (
            <Timestamps
              sx={{ mt: 2 }}
              createdAt={tvBrand.createdAt}
              updatedAt={tvBrand.updatedAt}
            />
          )}
        </PageContainer>
        <ConfirmDialog
          {...confirmDeleteProps}
          confirm={t('common.dialogs.permanently_delete')}
          deleteConfirm
          prompt={
            <Trans
              i18nKey="pages.tv_brands.detail.delete_dialog.prompt"
              components={{ b: <b />, span: <span /> }}
            />
          }
          title={t('pages.tv_brands.detail.delete_dialog.title')}
        />
      </>
    </LoadingPane>
  );
};
